import { Grid, Stack, Tab, Tabs } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { PersistedTabPanel } from "../../../../../../../common/PersistedTabPanel";
import type { ChartOfAccounts, MeasureDescriptor } from "../../../../../../../../api/types/svTypes.ts";
import { MeasureUnitTable } from "../../../../../../../../api/types/svTypes.ts";
import { selectAccountBookSettings } from "../../../../store/selectors/clientSettingsSelectors";
import { checkUnitTable } from "../../../../utilities/checkUnitTable.ts";
import LedgerAccountsList from "./LedgerAccountsList";
import { useChartOfAccountsContext } from "./contexts/ChartOfAccountsContext";
import type { Book } from "./getAccountingBooks";
import { getAccountingBooks } from "./getAccountingBooks";

interface LedgerAccountsListProps {
  chartOfAccounts: ChartOfAccounts | undefined;
  measure: MeasureDescriptor;
}
export default function LedgerAccountsMultiBookContainer({ chartOfAccounts, measure }: LedgerAccountsListProps) {
  const { selectedAccounts, scrollTargetAccount } = useChartOfAccountsContext();
  const accountingBookSettings = useSelector(selectAccountBookSettings);

  const hasGl = useMemo(() => {
    return Object.values(measure.units).some((unit) => checkUnitTable(unit.table, MeasureUnitTable.Gl));
  }, [measure]);

  const hasMemo = useMemo(() => {
    return Object.values(measure.units).some((unit) => checkUnitTable(unit.table, MeasureUnitTable.Memo));
  }, [measure]);

  const books = useMemo(
    () => getAccountingBooks(hasGl, hasMemo, accountingBookSettings),
    [accountingBookSettings, hasGl, hasMemo]
  );

  const [selectedTab, setSelectedTab] = useState(books[0]?.code);

  useEffect(() => {
    if (selectedAccounts.length > 0) {
      const isGl = chartOfAccounts?.gl.find((account) => selectedAccounts.includes(account.accountNo));
      if (isGl) {
        // TODO #209
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedTab(isGl.accountingBook);
        return;
      }
      const isMemo = chartOfAccounts?.memo.some((account) => selectedAccounts.includes(account.accountNo));
      if (isMemo) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedTab(null);
      }
    }
  }, [chartOfAccounts?.gl, chartOfAccounts?.memo, selectedAccounts]);

  useEffect(() => {
    if (scrollTargetAccount?.accountNo) {
      const isGl = chartOfAccounts?.gl.find((account) => scrollTargetAccount.accountNo === account.accountNo);
      if (isGl) {
        // TODO #209
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedTab(isGl.accountingBook);
        return;
      }
      const isMemo = chartOfAccounts?.memo.some((account) => scrollTargetAccount.accountNo === account.accountNo);
      if (isMemo) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedTab(null);
      }
    }
  }, [chartOfAccounts?.gl, chartOfAccounts?.memo, scrollTargetAccount?.accountNo]);

  const filterChartOfAccounts = useCallback(
    (book: Book) => {
      if (book.ledger === MeasureUnitTable.Memo) {
        return chartOfAccounts?.memo;
      }
      return chartOfAccounts?.gl.filter((item) => item.accountingBook === book.code);
    },
    [chartOfAccounts]
  );

  return (
    <Stack spacing={2} useFlexGap sx={{ py: 2.5, pr: 3, pl: 1.5, width: "100%" }}>
      <Tabs value={selectedTab} sx={{ pl: 2.5 }} onChange={(_, value: string | null) => setSelectedTab(value)}>
        {books.map((book) => (
          <Tab key={book.key} label={book.title} value={book.code} />
        ))}
      </Tabs>
      <Grid container sx={{ flex: 1, width: "100%" }}>
        {books.map((book) => (
          <PersistedTabPanel key={book.key} value={book.code} selectedTab={selectedTab}>
            <LedgerAccountsList chartOfAccounts={filterChartOfAccounts(book)} ledger={book.ledger} />
          </PersistedTabPanel>
        ))}
      </Grid>
    </Stack>
  );
}
